import { useEffect, useMemo, useRef, useState } from "react";
import { Markdown } from "./Markdown";
import { RowButton } from "./RowButton";
import { DOC_GROUPS, acharPagina, ancoras, buscarNoConteudo, type DocPage } from "../lib/docs-content";

/**
 * Area de documentacao de integracao dentro do produto.
 *
 * Substitui o site Docusaurus publico. O que o site dava e a tela antiga nao
 * tinha — navegacao por assunto, conteudo longo com ancoras e busca — esta aqui;
 * o que o site nao podia dar e este lugar pode e saber QUEM esta lendo, o que
 * abre caminho pra documentacao por convenio (fase 2).
 *
 * A secao "Referencia" nao e markdown: e o proprio console interativo, passado
 * por `referencia`, com Authorize, filtro por escopo e "Tente agora" intactos.
 */
export interface DocsShellProps {
  /** Eyebrow + titulo + subtitulo do topo. */
  titulo: string;
  subtitulo: string;
  /** Slug aberto (vem da rota). `undefined` abre a primeira pagina. */
  slug?: string;
  /** Troca de pagina — a rota que decide como navegar. */
  onNavegar: (slug: string) => void;
  /** Console interativo, renderizado quando a secao Referencia esta aberta. */
  referencia: React.ReactNode;
  /** Conteudo extra por perfil (fase 2: regras por convenio). */
  extras?: { slug: string; titulo: string; resumo: string; node: React.ReactNode }[];
}

const SLUG_REFERENCIA = "referencia";

export function DocsShell({ titulo, subtitulo, slug, onNavegar, referencia, extras = [] }: DocsShellProps) {
  // `busca` filtra a sidebar; `realce` é o termo que fica MARCADO no conteúdo. São
  // separados de propósito: ao clicar num resultado a caixa esvazia (a sidebar
  // volta ao normal) mas o realce persiste na página aberta — era isso que
  // faltava, a busca "não ficava marcada".
  const [busca, setBusca] = useState("");
  const [realce, setRealce] = useState("");
  const [ativa, setAtiva] = useState(0); // ocorrência em foco, p/ navegação
  const [totalHits, setTotalHits] = useState(0);
  const [ancoraAtiva, setAncoraAtiva] = useState(""); // seção em foco no índice
  const conteudoRef = useRef<HTMLDivElement>(null);
  // Enquanto uma rolagem disparada por clique no índice está animando, o
  // scroll-spy fica em silêncio — senão ele reagiria aos eventos de scroll da
  // própria animação e "brigaria" com o clique (o efeito de ir-e-voltar).
  const rolagemProg = useRef(false);
  const rolagemTimer = useRef<number | null>(null);

  const grupos = useMemo(
    () => [
      ...DOC_GROUPS,
      ...(extras.length
        ? [{ titulo: "Seu ambiente", paginas: extras.map((e) => ({ slug: e.slug, titulo: e.titulo, resumo: e.resumo, markdown: "" })) }]
        : []),
      { titulo: "Referência", paginas: [{ slug: SLUG_REFERENCIA, titulo: "Endpoints", resumo: "Catálogo completo e “Tente agora”", markdown: "" }] },
    ],
    [extras],
  );

  const atual = slug ?? DOC_GROUPS[0]!.paginas[0]!.slug;
  const paginaMd = acharPagina(atual);
  const extraAberto = extras.find((e) => e.slug === atual);
  const naReferencia = atual === SLUG_REFERENCIA;

  const resultados = useMemo(() => buscarNoConteudo(busca), [busca]);
  const indice = useMemo(() => (paginaMd ? ancoras(paginaMd.markdown) : []), [paginaMd]);

  // Trocou o termo realçado ou a página: recomeça da primeira ocorrência.
  useEffect(() => setAtiva(0), [realce, atual]);

  // Depois do render (as <mark data-doc-hit> já estão no DOM), pinta a ocorrência
  // em foco mais forte que as demais e a traz pra vista. A contagem sai do DOM, e
  // não do texto-fonte, porque o realce pula blocos de código e o markdown perde
  // sintaxe ao renderizar — contar na origem não bateria com o que se vê.
  const foco = totalHits ? ((ativa % totalHits) + totalHits) % totalHits : 0;
  useEffect(() => {
    const cont = conteudoRef.current;
    if (!cont || !realce) { setTotalHits(0); return; }
    const hits = Array.from(cont.querySelectorAll<HTMLElement>("[data-doc-hit]"));
    setTotalHits(hits.length);
    const alvo = hits.length ? ((ativa % hits.length) + hits.length) % hits.length : 0;
    hits.forEach((h, i) => {
      const on = i === alvo;
      h.style.background = on
        ? "color-mix(in srgb, var(--accent) 60%, transparent)"
        : "color-mix(in srgb, var(--accent) 30%, transparent)";
      h.style.outline = on ? "2px solid var(--accent)" : "";
    });
    hits[alvo]?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [realce, atual, ativa]);

  const irOcorrencia = (delta: number) =>
    setAtiva((a) => (totalHits ? (a + delta + totalHits) % totalHits : 0));
  const limparRealce = () => { setRealce(""); setBusca(""); };

  // Cada coluna rola por conta própria agora (o container do meio é o scroller),
  // então trocar de página tem que voltar o conteúdo ao topo — senão a página
  // nova abre na altura de rolagem da anterior. Roda antes do scroll pra
  // ocorrência realçada, que vem depois e vence quando há destaque.
  useEffect(() => { conteudoRef.current?.scrollTo({ top: 0 }); }, [atual]);

  // Scroll-spy do índice "Nesta página": conforme se rola o conteúdo, acompanha
  // qual seção (h2) está no topo da área visível e a marca no índice. Observa o
  // próprio container do conteúdo — que agora rola sozinho —, não a janela.
  useEffect(() => {
    const cont = conteudoRef.current;
    if (!cont || indice.length < 3) { setAncoraAtiva(""); return; }
    const calcular = () => {
      // Durante a rolagem de um clique, não computa: só reagenda a liberação
      // pra ~140ms depois do ÚLTIMO evento de scroll (quando a animação parou).
      if (rolagemProg.current) {
        if (rolagemTimer.current) window.clearTimeout(rolagemTimer.current);
        rolagemTimer.current = window.setTimeout(() => { rolagemProg.current = false; }, 140);
        return;
      }
      const heads = Array.from(cont.querySelectorAll<HTMLElement>("h2[id]"));
      if (!heads.length) return;
      // No fim da rolagem a última seção manda: ela pode nunca alcançar o topo
      // (não há conteúdo abaixo pra empurrá-la), e sem isso o spy ficava travado
      // na penúltima.
      if (cont.scrollTop + cont.clientHeight >= cont.scrollHeight - 4) {
        setAncoraAtiva(heads[heads.length - 1]!.id);
        return;
      }
      // Caso geral: a seção "vira ativa" ~96px antes de encostar no topo,
      // acompanhando a leitura em vez de trocar só quando o título some.
      const topo = cont.getBoundingClientRect().top;
      let ativo = heads[0]!.id;
      for (const h of heads) {
        if (h.getBoundingClientRect().top - topo <= 96) ativo = h.id;
        else break;
      }
      setAncoraAtiva(ativo);
    };
    calcular();
    cont.addEventListener("scroll", calcular, { passive: true });
    return () => {
      cont.removeEventListener("scroll", calcular);
      if (rolagemTimer.current) window.clearTimeout(rolagemTimer.current);
    };
  }, [indice]);

  /**
   * Resolve um link interno da doc (caminho estilo Docusaurus) pra um slug da
   * SPA e navega. Regras, cobrindo os caminhos que sobraram no conteúdo migrado:
   *   /referencia/<camada>  → console de referência DESTE portal (uma por portal)
   *   /guias/<slug>         → <slug>
   *   /<slug>  (ex: /erros) → <slug>
   *   /                     → primeira página
   * Se o destino não existe aqui, cai na visão geral em vez de abrir vazio.
   */
  const irParaLinkDoc = (href: string) => {
    const limpo = href.split(/[?#]/)[0]!.replace(/^\/+|\/+$/g, "");
    const segs = limpo ? limpo.split("/") : [];
    const inicio = DOC_GROUPS[0]!.paginas[0]!.slug;
    let alvo: string;
    if (segs.length === 0) alvo = inicio;
    else if (segs[0] === "referencia" || segs[0] === "reference") alvo = SLUG_REFERENCIA;
    else if (segs[0] === "guias" && segs[1]) alvo = segs[1]!;
    else alvo = segs[segs.length - 1]!;
    const existe = alvo === SLUG_REFERENCIA || !!acharPagina(alvo) || extras.some((e) => e.slug === alvo);
    onNavegar(existe ? alvo : inicio);
  };

  /** Rola até a seção dentro do container e a marca — usado pelo índice lateral. */
  const irParaAncora = (id: string) => {
    const cont = conteudoRef.current;
    const alvo = cont?.querySelector<HTMLElement>(`[id="${id}"]`);
    if (!cont || !alvo) return;
    setAncoraAtiva(id);          // marca já, sem esperar a animação
    rolagemProg.current = true;  // silencia o spy enquanto rola
    if (rolagemTimer.current) window.clearTimeout(rolagemTimer.current);
    // Fallback: libera em 700ms caso o scrollIntoView não gere evento algum
    // (alvo já visível). Enquanto houver scroll, o próprio spy reagenda p/ 140ms.
    rolagemTimer.current = window.setTimeout(() => { rolagemProg.current = false; }, 700);
    alvo.scrollIntoView({ block: "start", behavior: "smooth" });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18, height: "100%", minHeight: 0 }}>
      <header style={{ flexShrink: 0 }}>
        <span style={{ fontSize: 12, letterSpacing: "0.1em", fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase" }}>
          Documentação
        </span>
        <h1 style={{ margin: "4px 0 0", fontSize: "1.8rem", letterSpacing: "-0.02em" }}>{titulo}</h1>
        <p style={{ color: "var(--text-muted)", margin: "6px 0 0", maxWidth: 680 }}>{subtitulo}</p>
      </header>

      {/* Três colunas de altura cheia, cada uma com a própria rolagem vertical. */}
      <div style={{ display: "flex", gap: 24, alignItems: "stretch", flex: 1, minHeight: 0 }}>
        {/* ---------- Sidebar: busca fixa no topo, lista rola sozinha ---------- */}
        <nav style={{ flex: "0 0 232px", minWidth: 200, display: "flex", flexDirection: "column", gap: 12, minHeight: 0 }}>
          <input
            value={busca}
            onChange={(e) => {
              const v = e.target.value;
              setBusca(v);
              // Realce ao vivo: a página aberta acende enquanto você digita. Ao
              // clicar num resultado, esse realce viaja pra página destino — a
              // caixa esvazia mas o destaque não (é set direto, não via onChange).
              setRealce(v.trim().length >= 3 ? v.trim() : "");
            }}
            placeholder="Buscar na documentação…"
            style={{
              padding: "8px 10px", borderRadius: 8, border: "1px solid var(--border-strong)",
              background: "var(--surface)", color: "var(--text)", fontSize: 13, width: "100%",
            }}
          />

          <div className="atlas-doc-scroll" style={{ overflowY: "auto", minHeight: 0, flex: 1, display: "flex", flexDirection: "column", gap: 8, paddingRight: 6 }}>
          {busca.trim().length >= 3 ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <span style={{ fontSize: 11, color: "var(--text-dim)", textTransform: "uppercase", letterSpacing: ".06em" }}>
                {resultados.length} resultado{resultados.length === 1 ? "" : "s"}
              </span>
              {resultados.map((r) => (
                <RowButton
                  key={r.pagina.slug}
                  onClick={() => { setRealce(busca.trim()); onNavegar(r.pagina.slug); setBusca(""); }}
                  style={{ flexDirection: "column", alignItems: "flex-start", gap: 3, padding: "8px 10px", borderRadius: 8, border: "1px solid var(--border)" }}
                >
                  <span style={{ display: "flex", alignItems: "baseline", gap: 6, width: "100%" }}>
                    <b style={{ fontSize: 13 }}>{r.pagina.titulo}</b>
                    <span style={{ marginLeft: "auto", fontSize: 10.5, fontWeight: 700, color: "var(--text-dim)", whiteSpace: "nowrap" }}>
                      {r.ocorrencias}×
                    </span>
                  </span>
                  <span style={{ fontSize: 11, color: "var(--text-muted)", lineHeight: 1.4 }}>
                    {r.antes}
                    <mark style={{ background: "color-mix(in srgb, var(--accent) 32%, transparent)", color: "var(--text)", borderRadius: 3, padding: "0 1px", fontWeight: 600 }}>
                      {r.casado}
                    </mark>
                    {r.depois}
                  </span>
                </RowButton>
              ))}
              {resultados.length === 0 ? (
                <span style={{ fontSize: 12, color: "var(--text-muted)" }}>Nada encontrado no texto das páginas.</span>
              ) : null}
            </div>
          ) : (
            grupos.map((g) => (
              <div key={g.titulo} style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase", letterSpacing: ".06em", padding: "0 4px 4px" }}>
                  {g.titulo}
                </span>
                {g.paginas.map((p) => (
                  <ItemSidebar key={p.slug} pagina={p} ativo={p.slug === atual} onClick={() => onNavegar(p.slug)} />
                ))}
              </div>
            ))
          )}
          </div>
        </nav>

        {/* ---------- Conteúdo: coluna do meio, rola sozinha ---------- */}
        <div
          ref={conteudoRef}
          className="atlas-doc-scroll"
          style={{ flex: "1 1 520px", minWidth: 320, overflowY: "auto", minHeight: 0, paddingRight: 12, paddingBottom: paginaMd && indice.length > 2 ? 96 : 24 }}
        >
          {paginaMd && realce ? (
            <div style={{
              position: "sticky", top: 8, zIndex: 3, display: "flex", alignItems: "center", gap: 10,
              margin: "0 0 16px", padding: "7px 8px 7px 12px", borderRadius: 10,
              background: "var(--surface)", border: "1px solid var(--border-strong)",
              boxShadow: "0 4px 14px color-mix(in srgb, #000 14%, transparent)",
            }}>
              <span style={{ fontSize: 12.5, color: "var(--text-muted)" }}>
                Destacando <b style={{ color: "var(--text)" }}>“{realce}”</b>
              </span>
              <span style={{ display: "flex", alignItems: "center", gap: 6, marginLeft: "auto" }}>
                {totalHits > 0 ? (
                  <>
                    <span style={{ fontSize: 12, color: "var(--text-dim)", whiteSpace: "nowrap" }}>
                      {foco + 1} de {totalHits}
                    </span>
                    <BotaoOcorrencia titulo="Ocorrência anterior" onClick={() => irOcorrencia(-1)}>‹</BotaoOcorrencia>
                    <BotaoOcorrencia titulo="Próxima ocorrência" onClick={() => irOcorrencia(1)}>›</BotaoOcorrencia>
                  </>
                ) : (
                  <span style={{ fontSize: 12, color: "var(--text-dim)", whiteSpace: "nowrap" }}>não aparece nesta página</span>
                )}
                <BotaoOcorrencia titulo="Limpar destaque" onClick={limparRealce}>✕</BotaoOcorrencia>
              </span>
            </div>
          ) : null}
          {naReferencia ? referencia : extraAberto ? extraAberto.node : paginaMd ? (
            <Markdown realce={realce} onLinkInterno={irParaLinkDoc}>{paginaMd.markdown}</Markdown>
          ) : null}
        </div>

        {/* ---------- Índice da página: acompanha a rolagem e marca a seção ---------- */}
        {paginaMd && indice.length > 2 ? (
          <aside className="atlas-doc-scroll" style={{ flex: "0 0 176px", minHeight: 0, overflowY: "auto", display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={{
              position: "sticky", top: 0, background: "var(--bg)", paddingBottom: 4,
              fontSize: 11, fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase", letterSpacing: ".06em",
            }}>
              Nesta página
            </span>
            {indice.map((a) => {
              const on = ancoraAtiva === a.id;
              return (
                <a
                  key={a.id}
                  href={`#${a.id}`}
                  onClick={(e) => { e.preventDefault(); irParaAncora(a.id); }}
                  style={{
                    fontSize: 12, lineHeight: 1.5, textDecoration: "none",
                    color: on ? "var(--accent)" : "var(--text-muted)",
                    fontWeight: on ? 700 : 500,
                    paddingLeft: 10,
                    borderLeft: `2px solid ${on ? "var(--accent)" : "transparent"}`,
                    transition: "color .15s, border-color .15s",
                  }}
                >
                  {a.texto}
                </a>
              );
            })}
          </aside>
        ) : null}
      </div>
    </div>
  );
}

/** Botão compacto da barra de destaque (‹ › ✕). */
function BotaoOcorrencia({ children, titulo, onClick }: { children: React.ReactNode; titulo: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={titulo}
      aria-label={titulo}
      style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        width: 24, height: 24, borderRadius: 6, cursor: "pointer",
        border: "1px solid var(--border)", background: "var(--bg-elev-2)",
        color: "var(--text-muted)", fontSize: 14, lineHeight: 1, padding: 0,
      }}
    >
      {children}
    </button>
  );
}

function ItemSidebar({ pagina, ativo, onClick }: { pagina: DocPage; ativo: boolean; onClick: () => void }) {
  return (
    <RowButton
      onClick={onClick}
      titulo={pagina.resumo}
      style={{
        flexDirection: "column", alignItems: "flex-start", gap: 1, padding: "7px 10px", borderRadius: 8,
        background: ativo ? "color-mix(in srgb, var(--accent) 14%, transparent)" : "transparent",
        borderLeft: `2px solid ${ativo ? "var(--accent)" : "transparent"}`,
      }}
    >
      <span style={{ fontSize: 13, fontWeight: ativo ? 700 : 500 }}>{pagina.titulo}</span>
      <span style={{ fontSize: 11, color: "var(--text-muted)" }}>{pagina.resumo}</span>
    </RowButton>
  );
}
